import { apiRequest, ApiError, buildQuery } from './client';

export const mediaApi = {
  uploadImage: (file, kind = 'poll-option') => {
    const body = new FormData();
    body.append('file', file);
    return apiRequest(`/api/v1/me/media/images/${encodeURIComponent(kind)}`, { method: 'POST', body, timeoutMs: 120000 });
  },
  uploadAttachment: (file, kind) => {
    if (/\.zip$/i.test(file.name) || /zip/i.test(file.type)) {
      throw new ApiError('ZIP uploads are not supported', { code: 'ZIP_UPLOAD_DISABLED' });
    }
    if (kind === 'document' && !/\.(pdf|txt|docx|xlsx|pptx)$/i.test(file.name)) {
      throw new ApiError('Unsupported document', { code: 'UNSUPPORTED_ATTACHMENT' });
    }
    const body = new FormData();
    body.append('file', file);
    return apiRequest(`/api/v1/me/media/attachments/${encodeURIComponent(kind)}`, { method: 'POST', body, timeoutMs: 120000 });
  },
  reserve: (body) => apiRequest('/api/v1/me/media/uploads', { method: 'POST', body }),
  uploadContent: (id, file) => {
    const body = new FormData();
    body.append('file', file);
    return apiRequest(`/api/v1/me/media/uploads/${id}/content`, { method: 'POST', body, timeoutMs: 15 * 60 * 1000 });
  },
  complete: (id) => apiRequest(`/api/v1/me/media/uploads/${id}/complete`, { method: 'POST' }),
  get: (id) => apiRequest(`/api/v1/me/media/uploads/${id}`),
  list: (limit = 20) => apiRequest(`/api/v1/me/media/uploads${buildQuery({ limit })}`),
  cancel: (id) => apiRequest(`/api/v1/me/media/uploads/${id}`, { method: 'DELETE' }),
  async waitUntilReady(id, { intervalMs = 2500, timeoutMs = 15 * 60 * 1000, onStatus, signal } = {}) {
    const startedAt = Date.now();
    while (Date.now() - startedAt < timeoutMs) {
      if (signal?.aborted) throw new DOMException('Media processing cancelled', 'AbortError');
      const job = await this.get(id);
      onStatus?.(job);
      if (job.status === 'READY') return job;
      if (job.status === 'CANCELLED' || (job.status === 'FAILED' && !job.nextAttemptAt)) {
        const failure = /^(UPLOAD_[A-Z_]+):\s*(.*)$/.exec(job.failureReason || '');
        if (failure) throw new ApiError(failure[2], { code: failure[1] });
        throw new Error(job.failureReason || `Media processing ended with status ${job.status}`);
      }
      await new Promise((resolve, reject) => {
        const timer = setTimeout(resolve, intervalMs);
        signal?.addEventListener('abort', () => {
          clearTimeout(timer);
          reject(new DOMException('Media processing cancelled', 'AbortError'));
        }, { once: true });
      });
    }
    throw new Error('Media processing timed out');
  },
  async uploadReserved(job, file) {
    try {
      if (job.uploadMethod === 'POST') return await this.uploadContent(job.id, file);
      const response = await fetch(job.uploadUrl, {
        method: job.uploadMethod || 'PUT', body: file,
        headers: job.uploadHeaders || { 'Content-Type': file.type || 'video/mp4' },
      });
      if (!response.ok) throw new Error(`Media upload failed (${response.status})`);
      return await this.complete(job.id);
    } catch (error) {
      // A lost response can mean the upload actually completed. Never cancel processing work.
      try {
        const latest = await this.get(job.id);
        if (['UPLOADED', 'PROCESSING', 'READY'].includes(latest.status)
            || (latest.status === 'FAILED' && latest.nextAttemptAt)) return latest;
        if (latest.status === 'AWAITING_UPLOAD') await this.cancel(job.id);
      } catch (_) { /* Keep the original failure; server expiry recovers unreachable reservations. */ }
      throw error;
    }
  },
  async uploadVideo(file, durationSeconds, onStatus, options = {}) {
    let job = await this.reserve({
      type: 'VIDEO', fileName: file.name, contentType: file.type || 'video/mp4',
      sizeBytes: file.size, durationSeconds: Math.max(1, Math.ceil(durationSeconds)),
      audioMode: options.audioMode || 'ORIGINAL',
    });
    onStatus?.(job);
    job = await this.uploadReserved(job, file);
    onStatus?.(job);
    if (options.waitForReady === false) return job;
    return this.waitUntilReady(job.id, { ...options, onStatus });
  },
};
